import React from 'react';
import { Popover2 } from '@blueprintjs/popover2';
import { Button, Slider, RadioGroup, Radio, Divider } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';

/**
 * Mindmap Settings Popover
 * Provides controls for customizing mindmap layout with sliders
 */
const MindmapSettingsPopover: React.FC = observer(() => {
  const appStore = useAppStore();
  const { preferences, theme: themeStore } = appStore;
  
  // Get effective theme
  const isDark = themeStore.isDark;

  const handleCompactnessChange = (value: number) => {
    preferences.setMindmapCompactness(value);
  };

  const handleNodeSizeChange = (event: React.FormEvent<HTMLInputElement>) => {
    const size = (event.currentTarget as HTMLInputElement).value as 'small' | 'medium' | 'large';
    preferences.setMindmapNodeSize(size);
  };

  const handleNodeSpacingChange = (value: number) => {
    preferences.setMindmapNodeSpacing(value);
  };

  const popoverContent = (
    <div style={{ 
      padding: '16px', 
      width: '320px',
      backgroundColor: isDark ? '#2f343c' : '#ffffff',
      borderRadius: '4px',
      boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
      color: isDark ? '#f6f7f9' : '#1f2937'
    }}>
      <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '14px', fontWeight: 600, color: isDark ? '#f6f7f9' : '#1f2937' }}>
        Mindmap Layout Settings
      </h4>

      {/* Compactness Slider */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '12px', 
          fontWeight: 600,
          textTransform: 'uppercase',
          color: isDark ? '#738091' : '#5c7080'
        }}>
          Compactness: {preferences.mindmapCompactness.toFixed(1)}x
        </label>
        <Slider
          min={0.5}
          max={2.0}
          stepSize={0.1}
          labelStepSize={0.5}
          value={preferences.mindmapCompactness}
          onChange={handleCompactnessChange}
          showTrackFill={true}
        />
        <div style={{ fontSize: '11px', color: isDark ? '#738091' : '#999', marginTop: '4px' }}>
          Controls overall spacing density
        </div>
      </div>

      <Divider />

      {/* Node Spacing Slider */}
      <div style={{ marginBottom: '20px', marginTop: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '12px', 
          fontWeight: 600,
          textTransform: 'uppercase',
          color: isDark ? '#738091' : '#5c7080'
        }}>
          Node Spacing: {preferences.mindmapNodeSpacing}px
        </label>
        <Slider
          min={320}
          max={1000}
          stepSize={20}
          labelStepSize={100}
          value={preferences.mindmapNodeSpacing}
          onChange={handleNodeSpacingChange}
          showTrackFill={true}
        />
        <div style={{ fontSize: '11px', color: isDark ? '#738091' : '#999', marginTop: '4px' }}>
          Distance between nodes
        </div>
      </div>

      <Divider />

      {/* Node Size Radio Buttons */}
      <div style={{ marginTop: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '12px', 
          fontWeight: 600,
          textTransform: 'uppercase',
          color: isDark ? '#738091' : '#5c7080'
        }}>
          Node Size
        </label>
        <RadioGroup
          onChange={handleNodeSizeChange}
          selectedValue={preferences.mindmapNodeSize}
          inline={true}
        >
          <Radio label="Small" value="small" />
          <Radio label="Medium" value="medium" />
          <Radio label="Large" value="large" />
        </RadioGroup>
        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
          Base size for all nodes
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'absolute',
      top: '16px',
      right: '16px',
      zIndex: 1000,
    }}>
      <Popover2
        content={popoverContent}
        interactionKind="click"
        placement="bottom-end"
        minimal={false}
        hasBackdrop={false}
        enforceFocus={false}
        autoFocus={false}
        usePortal={true}
        portalContainer={document.body}
        popoverClassName={isDark ? 'bp5-dark mindmap-settings-popover' : 'mindmap-settings-popover'}
        popperOptions={{
          strategy: 'fixed', // Use fixed positioning to avoid issues with transforms
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                rootBoundary: 'viewport',
                padding: 8
              }
            },
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: ['bottom-start', 'top-end', 'top-start']
              }
            }
          ]
        }}
      >
        <Button
          icon={IconNames.COG}
          minimal={true}
          title="Mindmap Layout Settings"
          style={{
            backgroundColor: isDark ? '#2f343c' : '#ffffff',
            boxShadow: isDark ? '0 2px 4px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
            color: isDark ? '#f6f7f9' : undefined
          }}
        />
      </Popover2>
    </div>
  );
});

export default MindmapSettingsPopover;

